import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  CustomPricing,
  CustomPricingInput,
  CustomPricingUpdate,
} from '../../models/CustomPricing';
import customPricingService from '../../services/customPricingService';

const queryKey = (carrierId: number) => ['customPricing', carrierId];

export const useCustomPricing = (carrierId?: number) => {
  const { data, isLoading, status } = useQuery(
    queryKey(carrierId!),
    () => customPricingService.getAll(carrierId!),
    {
      enabled: !!carrierId,
    }
  );

  return {
    configs: data || [],
    isLoading,
    status,
  };
};

export const useCustomPricingCreate = (carrierId: number) => {
  const queryClient = useQueryClient();

  return useMutation<CustomPricing, Error, CustomPricingInput>(
    (body) => customPricingService.create(carrierId, body),
    {
      onSuccess: (data) => {
        queryClient.setQueryData<CustomPricing[]>(
          queryKey(carrierId),
          (configs) => [...(configs || []), data]
        );
      },
    }
  );
};

export const useCustomPricingUpdate = (carrierId: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    CustomPricing,
    Error,
    { id: number; body: CustomPricingUpdate }
  >(({ body, id }) => customPricingService.update(id, body), {
    onSuccess: (data) => {
      queryClient.setQueryData<CustomPricing[]>(
        queryKey(carrierId),
        (configs) => configs?.map((c) => (c.id === data.id ? data : c)) || []
      );
    },
  });
};

export const useCustomPricingRemove = (carrierId: number) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, number>(
    (id) => customPricingService.remove(id),
    {
      onSuccess: (_data, id) => {
        queryClient.setQueryData<CustomPricing[]>(
          queryKey(carrierId),
          (configs) => configs?.filter((c) => c.id !== id) || []
        );
      },
    }
  );
};
